import { NavigatorNetworkError } from './fetch-documents';

// Despite using `any` here this is actually completely type safe
// as this is stepping stone type for inference
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Fn = (...args: any[]) => any;

/**
 * Wraps an existing promise with a new one that will periodically check the network conditions and reject the promise
 * if we're currently offline.
 *
 * We also have an option to swallow a promise rejection if we're offline which defaults to true.
 */
const wrapPromise = <T>(promise: Promise<T>, swallowRejectionIfOffline = true): Promise<T> =>
  new Promise((resolve, reject) => {
    // This likely isn't required since we can't really resolve or reject a settled promise
    // but it's here for completeness
    let resolved = false;

    // TODO: See if there is a nicer way to do this kind of event ticking
    // TODO: `requestAnimationFrame` and `setImmediate` are also not really ideal
    // TODO: but since they are the primary options we're remaining with setInterval
    // TODO: to avoid an extra method wrapping the above.
    const interval = setInterval(() => {
      if (!navigator.onLine) {
        clearInterval(interval);

        resolved = true;
        reject(new NavigatorNetworkError());
      }
    }, 10);

    promise
      .then((result) => {
        clearInterval(interval);
        if (!resolved) {
          resolved = true;

          resolve(result);
        }
      })
      .catch((err) => {
        clearInterval(interval);
        if (!resolved) {
          resolved = true;

          // If we're offline, we assume the rejection occurred because of a network
          // error and thus replace it with a NavigatorNetworkError
          !navigator.onLine && swallowRejectionIfOffline
            ? reject(new NavigatorNetworkError())
            : reject(err);
        }
      });
  });

/**
 * withOfflineException wraps a method in the same way that `debounce` or `throttle` might. This wrapped function
 * then checks the current network conditions using `navigator.onLine` and if the network is offline, it raises
 * an exception. Additionally, if a promise is returned by the method, the wrapped function will also wrap the promise
 * periodically checking the network conditions and aborting the promise if the network is offline.
 *
 * We utilise a few fancy types here to ensure that we remain fully typed all the way down to the method we're wrapping.
 */
export const withOfflineException = <T extends Fn>(
  fn: T,
  swallowRejectionIfOffline = true,
  // eslint-disable-next-line arrow-body-style
): ((...args: Parameters<T>) => ReturnType<T>) => {
  // The `() => () => {}` syntax for factories really reduces code legibility
  return (...args) => {
    if (!navigator.onLine) {
      throw new NavigatorNetworkError();
    }

    const result = fn(...args);

    // If it's a then-able (Promise) then we will attempt some extra handling
    if (typeof result === 'object' && typeof result.then === 'function') {
      return wrapPromise(result, swallowRejectionIfOffline);
    }

    return result;
  };
};

export const withOfflineExceptionAsync = <T extends Fn>(
  fn: T,
  swallowRejectionIfOffline = true,
  // eslint-disable-next-line arrow-body-style
): ((...args: Parameters<T>) => ReturnType<T>) => {
  // The `() => () => {}` syntax for factories really reduces code legibility
  return (...args) => {
    if (!navigator.onLine) {
      return Promise.reject(new NavigatorNetworkError());
    }

    const result = fn(...args);

    // If it's a then-able (Promise) then we will attempt some extra handling
    if (typeof result === 'object' && typeof result.then === 'function') {
      return wrapPromise(result, swallowRejectionIfOffline);
    }

    return result;
  };
};
